import { DialogProgrammatic as Dialog } from 'buefy';
import store from '../store';
import * as authActions from '../store/modules/auth/actions';
import * as contractorActions from '../store/modules/contractor/actions';
import * as partnerActions from '../store/modules/partner/actions';
import accessControl, { ROLE } from './accessControl';
import { SET_SPLASH } from '../store/mutations';
import { LOAD } from '../store/modules/user/mutations';
import { NotAuthorizedException, AccessDeniedException } from './exception';
import permission from '../assets/Permissions';

const LOGIN_ACTION = `auth2/${authActions.USER}`;
const USER_LOAD_MUTATION = `user/${LOAD}`;

const LOAD_CONTRACTOR_ACTION = `contractor/${contractorActions.CONTRACTOR}`;

const LOAD_PARTNER_ACTION = `partner/${partnerActions.PARTNER}`;


/**
 * Collect access by path from permissions
 * @param path
 * @param obj
 * @param privileges
 */
function collectAccessByPath(path, obj, privileges) {
  if (Object.prototype.toString.call(obj) === '[object Object]') {
    if (obj.access) {
      privileges.push(obj.access);
    } else {
      Object.entries(obj).forEach(([key, value]) => {
        if (path.length === 0 || path.indexOf(`/${key}`, 0) === 0) {
          collectAccessByPath(path.substring(key.length + 1, path.length), value, privileges);
        }
      });
    }
  }
}


/**
 * Check access by accessControl urls list
 * @param to
 */
function checkAccess(to) {
  // check route for each accessList until not match by path
  for (let i = 0; i < accessControl.length; i += 1) {
    const access = accessControl[i];
    if (access.path.test(to.path)) {
      // if route accessible for anonymous user break checking
      if (access.role === ROLE.IS_AUTHENTICATED_ANONYMOUSLY) break;

      // check if user authenticated
      if (!store.state.auth2.isAuth) throw new NotAuthorizedException('Authorization is required');

      if (to.path === '/') break;

      // if route accessible for managers then check auth roles
      if (access.role === ROLE.IS_MANAGER) {
        const privileges = [];
        let hasPrivilege = false;
        collectAccessByPath(to.path, permission, privileges);
        privileges.push({ resource: to.name, code: 'access' });

        privileges.forEach((privilege) => {
          if (store.getters.authAuthenticator(privilege)) {
            hasPrivilege = true;
          }
        });

        if (!hasPrivilege) {
          throw new AccessDeniedException('Access denied');
        } else {
          break;
        }
      }

      let nameResource = to.name;
      if (to.meta.resource) {
        nameResource = to.meta.resource;
      }
      // otherwise check user or manage privileges
      if (!store.getters.userAuthenticator({ resource: nameResource, code: 'access' })) {
        if (!store.getters.authAuthenticator({ resource: nameResource, code: 'manage_access' })) {
          throw new AccessDeniedException('Access denied');
        }
      }

      break;
    }
  }
}

/**
 * Init user store
 * @param user
 */
async function initUser(user) {
  // init user store
  store.commit(USER_LOAD_MUTATION, user);
  // init contractor store
  if (user.contractor) {
    await store.dispatch(LOAD_CONTRACTOR_ACTION, user.contractor);
  }
  if (user.partnerId) {
    await store.dispatch(LOAD_PARTNER_ACTION, user.partnerId);
  }
}

/**
 * Get user from server by token
 * If token not set then redirect to login page
 * @param to
 * @param from
 * @param next
 */
function authorization(to, from, next) {
  store.commit(SET_SPLASH, true);

  // also init auth store
  store.dispatch(LOGIN_ACTION)
    .then((user) => {
      initUser(user).finally(() => {
        checkAccess(to);
        next();
      });
    })
    .catch((e) => {
      if (e instanceof AccessDeniedException) {
        Dialog.alert({ type: 'is-warning', title: e.code, message: e.message });

        next('/');
      } else {
        next({ name: 'login' });
      }
    }).finally(() => store.commit(SET_SPLASH, false));
}

/**
 * Navigation guard, check authorization and access
 * @param to
 * @param from
 * @param next
 */
export default (to, from, next) => {
  try {
    checkAccess(to);
    next();
  } catch (e) {
    if (e instanceof NotAuthorizedException) {
      authorization(to, from, next);
    } else if (e instanceof AccessDeniedException) {
      Dialog.alert({ type: 'is-warning', title: e.code, message: e.message });
    }
  }
};
